/**
 * AES-256-GCM decryption for sensitive URL parameters.
 *
 * The external app (e.g. VBA) encrypts the query payload with a shared
 * 256-bit key and puts the result in a single `d` query param. This module
 * decrypts it in the browser using the native Web Crypto API.
 *
 * Wire format (before base64url):
 *   IV (12 bytes) || ciphertext || GCM auth tag (16 bytes)
 *
 * base64url is only the transport wrapper so the bytes survive in a URL — the
 * confidentiality comes from AES-GCM, not from the encoding. GCM also provides
 * integrity: a tampered payload fails to decrypt instead of returning garbage.
 *
 * NOTE: the key ships in the frontend bundle, so it is extractable by a
 * determined client. This protects the data in transit / in the URL / in the
 * browser history, which is the stated goal, but is not a substitute for a
 * server-side secret. See the token pattern if stronger guarantees are needed.
 */

const IV_LENGTH = 12;

/** Converts a hex string (64 chars = 32 bytes) to a Uint8Array. */
function hexToBytes(hex: string): Uint8Array {
  const clean = hex.trim();
  if (clean.length % 2 !== 0) {
    throw new Error('Invalid hex key length');
  }
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.substr(i * 2, 2), 16);
  }
  return out;
}

/** Decodes a base64url string (no padding) to a Uint8Array. */
function base64UrlToBytes(b64url: string): Uint8Array {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

/**
 * Decrypts a base64url AES-256-GCM payload and returns the plaintext string.
 * Throws if the key is wrong, the payload is malformed, or it was tampered with.
 */
export async function decryptUrlPayload(b64urlPayload: string, keyHex: string): Promise<string> {
  const raw = base64UrlToBytes(b64urlPayload);
  if (raw.length <= IV_LENGTH) {
    throw new Error('Encrypted payload too short');
  }

  const iv = raw.slice(0, IV_LENGTH);
  const cipherAndTag = raw.slice(IV_LENGTH); // ciphertext || 16-byte tag

  const key = await crypto.subtle.importKey(
    'raw',
    hexToBytes(keyHex) as BufferSource,
    { name: 'AES-GCM' },
    false,
    ['decrypt'],
  );

  const plainBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    cipherAndTag as BufferSource,
  );

  return new TextDecoder().decode(plainBuffer);
}
